import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import {
  AppliedStrength,
  useAcmgClassification,
  strengthOptionsForCriterion,
  getStrengthWeight,
  AcmgCriterion,
  type AcmgClassification,
} from './hooks/useAcmgClassification';

/** Payload ready for a future ACMG persist endpoint (BE not supported yet). */
export type AcmgSubmitPayload = {
  classification: AcmgClassification;
  score: number;
  criteria: Record<string, AppliedStrength>;
  uploaded_variation?: string;
};

type AcmgClassificationPanelProps = {
  variantDetails?: { uploaded_variation?: string };
  /**
   * Optional override. When omitted, Submit shows that backend persistence
   * is not supported yet (keeps the button wired for a future API).
   */
  onSubmit?: (payload: AcmgSubmitPayload) => void | Promise<void>;
  submitPending?: boolean;
};

type AcmgVerdict = ReturnType<typeof useAcmgClassification>['classification'];

const getVerdictTone = (verdict: AcmgVerdict, theme: Theme) => {
  let color = theme.palette.warning.main;
  if (verdict === 'Pathogenic') color = theme.palette.error.main;
  if (verdict === 'Likely pathogenic') color = theme.palette.warning.main;
  if (verdict === 'Likely Benign' || verdict === 'Benign') {
    color = theme.palette.success.main;
  }

  return {
    bg: alpha(color, theme.palette.mode === 'dark' ? 0.14 : 0.08),
    fg: color,
    border: alpha(color, theme.palette.mode === 'dark' ? 0.68 : 0.48),
  };
};

const getCriterionAccent = (criterion: AcmgCriterion, theme: Theme) => {
  if (theme.palette.mode === 'light' && criterion.color) return criterion.color;
  return criterion.bucket === 'pathogenic'
    ? theme.palette.error.main
    : theme.palette.success.main;
};

const strengthShortLabel = (s: AppliedStrength) => {
  if (s === 'very_strong') return 'Very strong';
  if (s === 'standalone') return 'Stand alone';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const strengthAbbrev = (s: AppliedStrength) => {
  if (s === 'very_strong') return 'VS';
  if (s === 'standalone') return 'SA';
  if (s === 'supporting') return 'P';
  if (s === 'moderate') return 'M';
  return 'S';
};

const CriterionChip = ({
  criterion,
  applied,
  strength,
  onToggle,
}: {
  criterion: AcmgCriterion;
  applied: boolean;
  strength: AppliedStrength;
  onToggle: () => void;
}) => {
  const theme = useTheme();
  const accent = getCriterionAccent(criterion, theme);
  const appliedText = theme.palette.getContrastText(accent);
  const label = applied
    ? `${criterion.name} ${strengthAbbrev(strength)}`
    : criterion.name;

  return (
    <Tooltip title={criterion.description} placement="top" arrow>
      <Chip
        size="small"
        label={label}
        onClick={onToggle}
        variant={applied ? 'filled' : 'outlined'}
        sx={{
          fontWeight: 700,
          letterSpacing: 0.02,
          cursor: 'pointer',
          color: applied ? appliedText : 'text.secondary',
          bgcolor: applied ? accent : 'background.paper',
          borderColor: applied ? accent : 'divider',
          '&:hover': {
            bgcolor: applied ? accent : alpha(accent, 0.08),
            borderColor: accent,
            color: applied ? appliedText : 'text.primary',
          },
        }}
      />
    </Tooltip>
  );
};

const AppliedRuleRow = ({
  criterion,
  strength,
  onStrengthChange,
  onRemove,
}: {
  criterion: AcmgCriterion;
  strength: AppliedStrength;
  onStrengthChange: (s: AppliedStrength) => void;
  onRemove: () => void;
}) => {
  const theme = useTheme();
  const accent = getCriterionAccent(criterion, theme);
  const options = strengthOptionsForCriterion(criterion);
  const signedWeight =
    criterion.bucket === 'pathogenic'
      ? `+${getStrengthWeight(strength)}`
      : `−${getStrengthWeight(strength)}`;

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: alpha(accent, 0.34),
        bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.12 : 0.06),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minWidth: 0 }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: accent, flexShrink: 0 }}
        >
          {criterion.name}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: accent, flexShrink: 0, minWidth: 22 }}
        >
          {signedWeight}
        </Typography>
        <Select
          size="small"
          value={strength}
          onChange={(e) => onStrengthChange(e.target.value as AppliedStrength)}
          sx={{
            ml: 'auto',
            height: 28,
            fontSize: '0.75rem',
            bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0.5, pr: 3 },
          }}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>
              {strengthShortLabel(opt)}
            </MenuItem>
          ))}
        </Select>
        <IconButton
          size="small"
          aria-label={`Remove ${criterion.name}`}
          onClick={onRemove}
          sx={{ p: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={criterion.description}
        display="block"
        sx={{ mt: 0.25 }}
      >
        {criterion.description}
      </Typography>
    </Box>
  );
};

const EvidenceColumn = ({
  title,
  tone,
  items,
  selections,
  isApplied,
  setApplied,
  setStrength,
}: {
  title: string;
  tone: 'error' | 'success';
  items: AcmgCriterion[];
  selections: Record<string, AppliedStrength>;
  isApplied: (code: string) => boolean;
  setApplied: (code: string, v: boolean) => void;
  setStrength: (code: string, s: AppliedStrength) => void;
}) => {
  const theme = useTheme();
  const accent = theme.palette[tone].main;
  const appliedItems = items.filter((c) => isApplied(c.name));

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ color: accent, mb: 1 }}
      >
        {title}
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
        {items.map((criterion) => {
          const applied = isApplied(criterion.name);
          const strength =
            selections[criterion.name] ?? criterion.defaultStrength;
          return (
            <CriterionChip
              key={criterion.name}
              criterion={criterion}
              applied={applied}
              strength={strength}
              onToggle={() => setApplied(criterion.name, !applied)}
            />
          );
        })}
      </Stack>
      {items.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No rules match this search.
        </Typography>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1.5, mb: 0.75, fontWeight: 600 }}
      >
        Applied ({appliedItems.length})
      </Typography>
      <Stack spacing={0.75}>
        {appliedItems.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            Click a code to apply it. Hover for the rule text.
          </Typography>
        )}
        {appliedItems.map((criterion) => (
          <AppliedRuleRow
            key={criterion.name}
            criterion={criterion}
            strength={selections[criterion.name] ?? criterion.defaultStrength}
            onStrengthChange={(s) => setStrength(criterion.name, s)}
            onRemove={() => setApplied(criterion.name, false)}
          />
        ))}
      </Stack>
    </Box>
  );
};

const AcmgClassificationPanel = ({
  variantDetails,
  onSubmit,
  submitPending = false,
}: AcmgClassificationPanelProps) => {
  const theme = useTheme();
  const {
    filteredCriteria,
    selections,
    search,
    setSearch,
    counts,
    score,
    classification,
    isApplied,
    setApplied,
    setStrength,
    resetSelection,
    appliedCount,
  } = useAcmgClassification();

  const pathogenicItems = useMemo(
    () => filteredCriteria.filter((c) => c.bucket === 'pathogenic'),
    [filteredCriteria],
  );
  const benignItems = useMemo(
    () => filteredCriteria.filter((c) => c.bucket === 'benign'),
    [filteredCriteria],
  );

  const tone = getVerdictTone(classification, theme);
  const variantLabel = variantDetails?.uploaded_variation;

  const handleSubmit = () => {
    const payload: AcmgSubmitPayload = {
      classification,
      score,
      criteria: selections,
      uploaded_variation: variantLabel,
    };

    if (onSubmit) {
      onSubmit(payload);
      return;
    }

    // Backend ACMG persistence is not available yet; keep the control wired
    // so callers can supply `onSubmit` once the endpoint exists.
    enqueueSnackbar(
      'ACMG classification submit is not supported by the backend yet.',
      { variant: 'info' },
    );
  };

  return (
    <Stack spacing={1.5} sx={{ pt: 1.5 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: tone.bg,
          border: '1px solid',
          borderColor: tone.border,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ color: tone.fg, lineHeight: 1.2 }}
            >
              {classification}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Score {score}
              {variantLabel ? ` · ${variantLabel}` : ''}
              {' · '}
              supporting 1, moderate 2, strong 4, very strong 8
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              label={`Score ${score}`}
              sx={{ fontWeight: 700, bgcolor: 'background.paper' }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={appliedCount === 0 || submitPending}
            >
              Submit
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={resetSelection}
              disabled={appliedCount === 0 || submitPending}
              sx={{ bgcolor: 'background.paper' }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search rules (PM2, de novo, gnomAD…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.paper',
            },
          }}
        />
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`PVS ${counts.pvs}`} />
          <Chip size="small" variant="outlined" label={`PS ${counts.ps}`} />
          <Chip size="small" variant="outlined" label={`PM ${counts.pm}`} />
          <Chip size="small" variant="outlined" label={`PP ${counts.pp}`} />
          <Chip size="small" variant="outlined" label={`BA ${counts.ba}`} />
          <Chip size="small" variant="outlined" label={`BS ${counts.bs}`} />
          <Chip size="small" variant="outlined" label={`BP ${counts.bp}`} />
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems="stretch"
      >
        <EvidenceColumn
          title="Pathogenic"
          tone="error"
          items={pathogenicItems}
          selections={selections}
          isApplied={isApplied}
          setApplied={setApplied}
          setStrength={setStrength}
        />
        <EvidenceColumn
          title="Benign"
          tone="success"
          items={benignItems}
          selections={selections}
          isApplied={isApplied}
          setApplied={setApplied}
          setStrength={setStrength}
        />
      </Stack>
    </Stack>
  );
};

export default AcmgClassificationPanel;
